import type { HistoricalFile } from "@/types";

/** Sentinel for "no filter applied" on string select filters. */
export const HISTORY_FILTER_ALL = "All";

export type HistorySortOrder = "newest" | "oldest";

export interface HistoryFilterState {
  /** Provider UUID; HISTORY_FILTER_ALL = no filter. Ignored when page is provider-scoped. */
  providerId: string;
  /** Schema UUID; HISTORY_FILTER_ALL = no filter. */
  schemaId: string;
  /** kodex_files.quality_status; HISTORY_FILTER_ALL = no filter. */
  qualityStatus: string;
  /** Sort by updatedAt. */
  sortOrder: HistorySortOrder;
}

export interface HistoryFilterOption {
  value: string;
  label: string;
}

export const DEFAULT_HISTORY_FILTER_STATE: HistoryFilterState = {
  providerId: HISTORY_FILTER_ALL,
  schemaId: HISTORY_FILTER_ALL,
  qualityStatus: HISTORY_FILTER_ALL,
  sortOrder: "newest",
};

/** True when the file passed DQ and was promoted to gold (status + quality_status). */
export function canDownloadMappedFile(
  file: Pick<HistoricalFile, "status" | "qualityStatus">
): boolean {
  return file.status === "complete" && file.qualityStatus === "clean";
}

/** Parse updatedAt for sorting; invalid/missing timestamps sort as oldest (0). */
function updatedAtMs(iso: string | null | undefined): number {
  if (!iso) return 0;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? 0 : ms;
}

/**
 * Unique schema options from loaded history files.
 * Labels are schema IDs (documents API has no schema_name join in this slice).
 */
export function uniqueSchemaOptions(files: HistoricalFile[]): HistoryFilterOption[] {
  const ids = [...new Set(files.map((f) => f.schemaId).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b)
  );
  return ids.map((id) => ({ value: id, label: id }));
}

/** Unique quality_status options from loaded history files. */
export function uniqueQualityOptions(files: HistoricalFile[]): HistoryFilterOption[] {
  const statuses = [...new Set(files.map((f) => f.qualityStatus).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b)
  );
  return statuses.map((status) => ({ value: status, label: status }));
}

/**
 * Provider filter options for providers that appear on loaded files.
 * Value stays providerId; label prefers the name map, falling back to the raw UUID.
 * Sorted by label A–Z so the dropdown matches other provider UIs.
 */
export function providerOptionsForFiles(
  files: HistoricalFile[],
  nameById: Map<string, string>
): HistoryFilterOption[] {
  const ids = [...new Set(files.map((f) => f.providerId).filter(Boolean))];
  return ids
    .map((id) => ({ value: id, label: nameById.get(id) ?? id }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Apply client-side provider / schema / quality filters and sort by updatedAt.
 * When isProviderScoped, the providerId filter is ignored (list is already server-scoped).
 */
export function applyHistoryFilters(
  files: HistoricalFile[],
  state: HistoryFilterState,
  options: { isProviderScoped: boolean }
): HistoricalFile[] {
  let result = files;

  if (!options.isProviderScoped && state.providerId && state.providerId !== HISTORY_FILTER_ALL) {
    result = result.filter((f) => f.providerId === state.providerId);
  }

  if (state.schemaId && state.schemaId !== HISTORY_FILTER_ALL) {
    result = result.filter((f) => f.schemaId === state.schemaId);
  }

  if (state.qualityStatus && state.qualityStatus !== HISTORY_FILTER_ALL) {
    result = result.filter((f) => f.qualityStatus === state.qualityStatus);
  }

  const direction = state.sortOrder === "oldest" ? 1 : -1;
  return [...result].sort(
    (a, b) => (updatedAtMs(a.updatedAt) - updatedAtMs(b.updatedAt)) * direction
  );
}
